import { Dictionary } from 'vuex';

import { EntityModuleState } from '@/interfaces/entityModuleState';
import { Filter } from '@/interfaces/filters/filter';
import { ListResponse } from '@/interfaces/list';
import { EntityResource } from '@/resources/entity.resource';
import { AbstractModule } from './abstract.module';

export class EntityModule<
  EntityType extends { id?: number },
  ResourceType extends EntityResource<EntityType>
> extends AbstractModule<ResourceType> {
  /**
   * Stan modułu encji
   *
   * @type {EntityModuleState<EntityType>}
   * @memberof EntityModule
   */
  public state: EntityModuleState<EntityType> = {
    list: [],
    count: 0,
    data: undefined,
    id: undefined,
    previousFilters: undefined
  };

  public mutations: Dictionary<(...args: any[]) => void> = {
    /**
     * Zapisuje listę do store
     *
     * @param localState stan lokalny
     * @param list lista encji
     */
    saveList(localState: EntityModuleState<EntityType>, list: EntityType[]): void {
      localState.list = list;
    },
    /**
     * Dodaje elementy do listy
     *
     * @param localState stan lokalny
     * @param list lista encji
     */
    appendList(localState: EntityModuleState<EntityType>, list: EntityType[]): void {
      localState.list = [...localState.list, ...list];
    },
    /**
     * Zapisuje liczbę encji do store
     *
     * @param localState stan lokalny
     * @param count liczba encji
     */
    saveCount(localState: EntityModuleState<EntityType>, count: any): void {
      localState.count = count;
    },
    /**
     * Dodaje liczbę encji do aktualnie zapisanej
     *
     * @param localState stan lokalny
     * @param count liczba encji
     */
    appendCount(localState: EntityModuleState<EntityType>, count: any): void {
      localState.count += count;
    },
    /**
     * Podnosi liczbę encji o 1
     *
     * @param localState stan lokalny
     */
    incrementCount(localState: EntityModuleState<EntityType>): void {
      localState.count += 1;
    },
    /**
     * Obniża liczbę encji o 1
     *
     * @param localState stan lokalny
     */
    decrementCount(localState: EntityModuleState<EntityType>): void {
      localState.count -= 1;
    },
    /**
     * Dodaje encję do listy
     *
     * @param localState stan lokalny
     * @param entity encja
     */
    add(localState: EntityModuleState<EntityType>, entity: EntityType): void {
      localState.list.unshift(entity);
    },
    /**
     * Usuwa encję z listy
     *
     * @param localState stan lokalny
     * @param id identyfikator encji
     */
    remove(localState: EntityModuleState<EntityType>, id: number): void {
      localState.list.splice(
        localState.list.findIndex((item: EntityType) => item.id === id),
        1
      );
    },
    /**
     * Aktualizuje encję
     *
     * @param localState stan lokalny
     * @param entity encja
     */
    update(localState: any, entity: EntityType): void {
      const index = localState.list.findIndex((item: EntityType) => item.id === entity.id);
      localState.list.splice(index, 1, entity);
    },
    /**
     * Zapisuje identyfikator encji do store
     *
     * @param localState stan lokalny
     * @param id identyfikator encji
     */
    setId(localState: any, id: number): void {
      localState.id = id;
    },
    /**
     * Zapisuje encję do store
     *
     * @param localState stan lokalny
     * @param entity encja
     */
    setEntity(localState: any, entity: EntityType): void {
      localState.data = { ...entity };
    },

    /**
     * Zapisuje ostatnio użyte filtry
     *
     * @param {*} localState
     * @param {Filter} filters
     */
    saveFilters(localState: any, filters: Filter): void {
      localState.previousFilters = filters;
    }
  };

  public actions: Dictionary<(...args: any[]) => Promise<void | any>> = {
    /**
     * Pobiera listę encji
     *
     * @param context context
     * @param filters filtry listy
     */
    list: (context: any, filters: Filter): Promise<ListResponse<EntityType>> => {
      const preventLoader = filters?.preventLoader;
      if (preventLoader) {
        filters = { ...filters, preventLoader: undefined };
      }

      return this.resource.list(filters, preventLoader).then(({ list, count }) => {
        context.commit('saveList', list);
        context.commit('saveCount', count);
        context.commit('saveFilters', filters);
        return { list, count };
      });
    },

    /**
     * Pobiera listę encji i dodaje ją do aktualnie pobranych encji
     *
     * @param context context
     * @param filters filtry listy
     */
    listAppend: (context: any, filters: Filter): Promise<ListResponse<EntityType>> => {
      return this.resource.list(filters).then(({ list, count }) => {
        context.commit('appendList', list);
        context.commit('appendCount', count);
        return { list, count };
      });
    },
    /**
     * Pobiera encję
     *
     * @param context context
     * @param id identyifkator encji
     */
    get: (context: any, id?: number): Promise<EntityType> => {
      return this.resource.get(id || context.state.id).then(entity => {
        context.commit('setEntity', entity);
        return entity;
      });
    },
    /**
     * Dodaje encję
     *
     * @param context context
     * @param data dane do wysłania do API
     */
    add: (context: any, data: any): Promise<void> => {
      return this.resource.add(data).then(id => {
        data.id = id;
        context.commit('add', data);
        context.commit('incrementCount');
        return data.id;
      });
    },
    /**
     * Usuwa encję
     *
     * @param context context
     * @param id identyfikator encji
     */
    remove: (context: any, id: number): Promise<void> => {
      return this.resource.delete(id).then(() => {
        context.commit('remove', id);
        context.commit('decrementCount');
      });
    },
    /**
     * Aktualizuje encję
     *
     * @param context context
     * @param data dane encji
     */
    update: (context: any, data: EntityType): Promise<number> => {
      return this.resource.update(data.id as number, data).then(id => {
        context.commit('update', data);
        context.commit('setEntity', data);
        return id;
      });
    }
  };

  public getters = {
    /**
     * Wybiera encję o podanym identyfikatorze z listy
     */
    byId: (localState: EntityModuleState<EntityType>) => (id: number): EntityType | undefined =>
      localState.list.find(entity => entity.id === id)
  };
}
